import { useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import { produce } from 'immer'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import styled from 'styled-components'

import * as spriteIcons from 'assets/svg/sprite'
import { useHeaderLayout } from 'context/headerLayoutContext'
import AnalyticsPeriodDropdown, {
  selectedToPeriod
} from 'features/AnalyticsPeriodDropdown/AnalyticsPeriodDropdown'
import BarChart, { defaultChartOptions } from 'features/BarChart/BarChart'
import CategoriesDropdown from 'features/CategoriesDropdown/CategoriesDropdown'
import { getAnalyticsChartData, valToDollar, valToPercent } from 'helpers/dataTransformations'
import { appendedArgsFn } from 'helpers/util'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getReportsAnalytics } from 'services/endpoints/analytic'
import { useUserProfile } from 'store'
import { useAnalyticsReportsCacheStore } from 'store/analyticsReportsCache'
import { type ReportFilter, type ReportTab } from 'types/api/analytic'
import type DeepPartial from 'types/utils/deeppartial'

import { AnalyticsCard } from '../cards/AnalyticsCard'

const defaultCardOptions = Object.freeze({
  valFormatFn: (val: any) => val,
  chartLabelFn: (val: any) => val,
  hasGraphBtn: true,
  icon: <spriteIcons.IconChartBlue />,
  absFix: true,
  additionalLabelKey: undefined
})
const chartDatasetOptions = Object.freeze({
  borderRadius: 15,
  barThickness: 'flex' as const
})
const reportCards = Object.freeze({
/*   engagement_rate: {
    ...defaultCardOptions,
    valFormatFn: valToPercent,
    chartLabelFn: (val: number) => valToPercent(val, 0),
    labelKey: 'EngagmentRate'
  }, */
  post_views: { ...defaultCardOptions, labelKey: 'PostViews' },
  likes: { ...defaultCardOptions, labelKey: 'likes' },
  comments: { ...defaultCardOptions, labelKey: 'comments' },
  posts: { ...defaultCardOptions, labelKey: 'posts' },
  premium_posts: { ...defaultCardOptions, labelKey: 'PremiumPosts' },
  stories: { ...defaultCardOptions, labelKey: 'stories' },
  video_completion_rate: {
    ...defaultCardOptions,
    labelKey: 'VideoCompletionRate',
    valFormatFn: valToPercent,
    chartLabelFn: appendedArgsFn(valToPercent, 2),
    additionalLabelKey: 'avgInParentheses'
  },
/*   average_fan_session: {
    ...defaultCardOptions,
    labelKey: 'AverageFanSession',
    valFormatFn: (val: number) => dayjs.duration(val, 'seconds').format('mm:ss'),
    chartLabelFn: (val: number) => dayjs.duration(val, 'seconds').format('mm:ss')
  }, */
  impressions: { ...defaultCardOptions, labelKey: 'impressions' },
  conversion_rate: {
    ...defaultCardOptions,
    labelKey: 'conversionRate',
    valFormatFn: valToPercent,
    chartLabelFn: appendedArgsFn(valToPercent, 2)
  },
  purchases: { ...defaultCardOptions, labelKey: 'purchases' },
  views: { ...defaultCardOptions, labelKey: 'views' },
  Rebills: { ...defaultCardOptions, labelKey: 'Rebills', absFix: false },
  profile_visits_subscribed: { ...defaultCardOptions, labelKey: 'Profit Visits (Subscribed)' },
  profile_visits_unsubscribed: { ...defaultCardOptions, labelKey: 'Profile Visits (Unsubscribed)' },
  rebills_rate_first_month_discount: { ...defaultCardOptions, labelKey: 'rebills_rate_first_month_discount' },
  rebills_rate_standard: { ...defaultCardOptions, labelKey: 'rebills_rate_standard' },
  subscription_conversion_rate: {
    ...defaultCardOptions,
    labelKey: 'subscription_conversion_rate',
    valFormatFn: valToPercent
  },
  free_trials: { ...defaultCardOptions, labelKey: 'Free Trials' },
  subscriptions: {
    ...defaultCardOptions,
    labelKey: 'subscriptions',
    valFormatFn: valToDollar,
    chartLabelFn: appendedArgsFn(valToDollar, 2)
  },
  new_subscriptions: { ...defaultCardOptions, labelKey: 'First Time Subscribers' },
  subscription_bundles: {
    ...defaultCardOptions,
    labelKey: 'Bundles',
    valFormatFn: valToDollar,
    chartLabelFn: appendedArgsFn(valToDollar, 2)
  },
  message_unlocks: {
    ...defaultCardOptions,
    valFormatFn: valToDollar,
    chartLabelFn: appendedArgsFn(valToDollar, 2),
    labelKey: 'messageUnlocks',
    icon: <spriteIcons.IconWallet2 />,
    absFix: false
  },
  mass_message_unlock_rate: {
    ...defaultCardOptions,
    labelKey: 'Unlock Rate - Mass Messages',
    valFormatFn: valToPercent,
    chartLabelFn: appendedArgsFn(valToPercent, 2)
  },
  single_message_unlock_rate: {
    ...defaultCardOptions,
    labelKey: 'Unlock Rate - Single Message',
    valFormatFn: valToPercent,
    chartLabelFn: appendedArgsFn(valToPercent, 2)
  },
  received_messages: { ...defaultCardOptions, labelKey: 'RecievedMessages' },
  replies_sent: { ...defaultCardOptions, labelKey: 'RepliesSent' },
  locked_messages_sent: { ...defaultCardOptions, labelKey: 'Messages - Locked' },
  mass_messages_sent: { ...defaultCardOptions, labelKey: 'Mass Messages' },
  open_rate: {
    ...defaultCardOptions,
    labelKey: 'OpenRate',
    valFormatFn: valToPercent,
    chartLabelFn: appendedArgsFn(valToPercent, 2)
  },
  sent_text_messages: { ...defaultCardOptions, labelKey: 'Messages - Text' }
})

export type IReportCardKeys = keyof typeof reportCards

export const Reports = () => {
  const { t } = useTranslation()

  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState(selectedToPeriod['7'])
  const [activeGraph, setActiveGraph] = useState<IReportCardKeys>()


  const [filter, setFilter] = useState<ReportFilter>('all')
  const [tab, setTab] = useState<ReportTab>('content')

  const selectedCategoryId = selectedCategory || undefined

  const roundedStart = useMemo(
    () => dayjs(selectedPeriod.start).startOf('day').toISOString(),
    [selectedPeriod.start]
  )
const roundedEnd = useMemo(
  () => dayjs(selectedPeriod.end).endOf('day').toISOString(),
  [selectedPeriod.end]
)

const cacheKey = useMemo(
  () =>
    `${tab}-${filter}-${selectedCategoryId ?? 'all'}-${roundedStart}-${roundedEnd}`,
  [tab, filter, selectedCategoryId, roundedStart, roundedEnd]
)

  const userProfile = useUserProfile()
  const username = userProfile?.username ?? ''

  const { getCache, setCache } = useAnalyticsReportsCacheStore()
  const cachedInitial = getCache(cacheKey)

  const reportsQueryKey = useMemo(
    () => ['reportsAnalytics', tab, filter, selectedCategoryId, roundedStart, roundedEnd],
    [tab, filter, selectedCategoryId, roundedStart, roundedEnd]
  )
  const { setBackgroundColor } = useHeaderLayout()
  const { data: reportsData } = useQuery(
    reportsQueryKey,
    async () => {
      const fresh = await getReportsAnalytics({
        tab,
        ...(tab === 'content' && { filter }),
        dateFrom: roundedStart,
        dateTo: roundedEnd,
        categoryId: selectedCategoryId
      })
      if (fresh?.data) {
        setCache(cacheKey, fresh.data)
      }
      return fresh
    },
    {
      initialData: cachedInitial ? { data: cachedInitial } : undefined,
      initialDataUpdatedAt: cachedInitial ? 0 : undefined,
      keepPreviousData: true,
      staleTime: 60_000,
      select(data) {
        if (!data?.data) return null

        return Object.keys(data.data).map(key => {
          const cardOptions = reportCards[key as IReportCardKeys]
          const cardData = data.data[key]
          if (!cardOptions || !cardData) return null

          return {
            key,
            hasGraphBtn: cardOptions.hasGraphBtn,
            labelKey: cardOptions.labelKey,
            value: cardOptions.valFormatFn(cardData?.sum),
            percentage: cardData?.percentage,
            salesCount: cardData?.count,
            additionalLabelKey: cardOptions.additionalLabelKey
          }
        })
      }
    }
  )
useEffect(() => {
  if (!reportsData?.length) return

  setBackgroundColor('#070707')

  return () => {
    setBackgroundColor(undefined)
  }
}, [reportsData, setBackgroundColor])
const graphQueryKey = useMemo(
  () => [
    'reportsAnalyticsGraph',
    tab,
    filter,
    activeGraph,
    selectedCategoryId,
    roundedStart,
    roundedEnd
  ],
  [
    tab,
    filter,
    activeGraph,
    selectedCategoryId,
    roundedStart,
    roundedEnd
  ]
)

const { data: graphData } = useQuery(
  graphQueryKey,
  async () => {
    if (!activeGraph) {
      return null
    }

    return await getAnalyticsChartData({
      fetchPromise: getReportsAnalytics({
        tab,
        ...(tab === 'content' && { filter }),
        dateFrom: roundedStart,
        dateTo: roundedEnd,
        categoryId: selectedCategoryId,
        chart: activeGraph
      })
    })
  },
  {
    enabled: !!activeGraph,
    keepPreviousData: true,
    staleTime: 60_000
  }
)

  const chartOptions = useMemo(() => {
    const formatX = (x: unknown) => (typeof x === 'string' ? x : String(x ?? ''))

    const formatY = (y: number) => {
      const raw =
        activeGraph
          ? reportCards[activeGraph]?.chartLabelFn?.(y)
          : y

      return typeof raw === 'string' ? raw : String(raw ?? '')
    }

    const options = defaultChartOptions((x, y) => ({
      x: formatX(x),
      y: formatY(y)
    }))

    return produce(options, (draft: DeepPartial<typeof options>) => {
      if (draft?.scales?.y?.ticks && activeGraph) {
        // Chart.js tick callback can receive string|number depending on adapter.
        // Force it through the same formatter.
        ;(draft.scales.y.ticks as any).callback = (value: unknown) => {
          const n =
            typeof value === 'number'
              ? value
              : Number(value)

          return formatY(Number.isFinite(n) ? n : 0)
        }
      }
    })
  }, [activeGraph])

  useEffect(() => {
    localStorage.setItem(
      'analytics-filters',
      JSON.stringify({
        selectedCategory,
        selectedPeriod,
        tab,
        filter
      })
    )
  }, [selectedCategory, selectedPeriod, tab, filter])

  useEffect(() => {
    const saved = localStorage.getItem('analytics-filters')
    if (saved) {
      const parsed = JSON.parse(saved)
      setSelectedCategory(parsed.selectedCategory ?? '')
      setSelectedPeriod(parsed.selectedPeriod ?? selectedToPeriod['7'])
      setTab(parsed.tab ?? 'content')
      setFilter(parsed.filter ?? 'all')
    }
  }, [])
useEffect(() => {
  let lastTop = window.scrollY
  let ticking = false
  let hasUserScrolled = false

  // Hide immediately on first downward movement
  const SHOW_AT_TOP_PX = 0
  const DELTA_PX = 1

  const setHeaderNavHidden = (hidden: boolean) => {
    document.body.classList.toggle('header-nav-hidden', hidden)
  }

  const enableScrollAnimation = () => {
    if (!document.body.classList.contains('header-scroll-animating')) {
      document.body.classList.add('header-scroll-animating')
    }
  }

  const resetHeaderNav = () => {
    document.body.classList.remove('header-nav-hidden')
    document.body.classList.remove('header-scroll-animating')
    document.body.classList.remove('header-nav-ready')
  }

  // Reset on mount
  resetHeaderNav()

  // Enable transitions after first paint only
  requestAnimationFrame(() => {
    document.body.classList.add('header-nav-ready')
  })

  const onScroll = () => {
    hasUserScrolled = true

    const top = window.scrollY
    const delta = top - lastTop

    if (!ticking) {
      ticking = true

      requestAnimationFrame(() => {
        ticking = false

        if (!hasUserScrolled) return

        enableScrollAnimation()

        // Fully visible only at exact top
        if (top <= SHOW_AT_TOP_PX) {
          setHeaderNavHidden(false)
          lastTop = top
          return
        }

        // Ignore micro jitter
        if (Math.abs(delta) < DELTA_PX) {
          lastTop = top
          return
        }

        // Hide immediately when scrolling down
        // Show immediately when scrolling up
        setHeaderNavHidden(delta > 0)

        lastTop = top
      })
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    window.removeEventListener('scroll', onScroll)
    resetHeaderNav()
  }
}, [])
  return (
    <Container className='reports-container'>
      {activeGraph && (
        <div className='analytics-chart-container'>
          <BarChart
            chartData={graphData}
            datasetOptions={chartDatasetOptions}
            options={chartOptions}
          />
        </div>
      )}

      <WithHeaderSection
        customClass={`analytics-reports-whs${activeGraph ? ' analytics-sales-whs--chart-open' : ''}`}
        headerSection={
          <>
            <div className='analytics-dropdown-container'>
              <div style={{ width: '50%', marginRight: '5px' }}>
                <CategoriesDropdown
                  username={username}
                  selectedCategoryId={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                />
              </div>

              <div style={{ width: '50%' }}>
                <AnalyticsPeriodDropdown
                  selectedPeriodId={'7'}
                  onSelectPeriod={setSelectedPeriod}
                />
              </div>
            </div>

            <div className='reports-overflow-container'>
              <div className='reports-links-container'>
                <div
                  className={`reports-link ${tab === 'content' ? 'reports-link-active' : ''}`}
                  onClick={() => setTab('content')}
                >
                  {t('feed')}
                </div>

                {!selectedCategoryId && (
                  <div
                    className={`reports-link ${tab === 'subscription' ? 'reports-link-active' : ''}`}
                    onClick={() => setTab('subscription')}
                  >
                    {t('subscriptions')}
                  </div>
                )}

                <div
                  className={`reports-link ${tab === 'messages' ? 'reports-link-active' : ''}`}
                  onClick={() => setTab('messages')}
                >
                  {t('messages')}
                </div>
              </div>
            </div>

            {tab === 'content' && (
              <div className='reports-types-container'>
                {['all', 'premium', 'videos', 'photos'].map(type => (
                  <div
                    key={type}
                    className={`report-type ${filter === type ? 'report-type-active' : ''}`}
                    onClick={() => setFilter(type as ReportFilter)}
                  >
                    {type === 'all' ? 'All' : t(type)}
                  </div>
                ))}
              </div>
            )}
          </>
        }
      >
        <div className='analytics-cards-container'>
          {reportsData?.map(
            cardData =>
              cardData && (
                <AnalyticsCard
                  key={cardData.key}
                  cardKey={cardData.key}
                  customClass='analytics-reports-card'
                  absFix={reportCards[cardData.key as IReportCardKeys].absFix}
                  icon={reportCards[cardData.key as IReportCardKeys].icon}
                  hasGraphBtn={cardData.hasGraphBtn}
                  activeGraph={activeGraph}
                  label={t(cardData.labelKey)}
                  additionalLabel={cardData.additionalLabelKey ? t(cardData.additionalLabelKey) : undefined}
                  value={cardData.value}
                  salesCount={cardData.salesCount}
                  percentage={cardData.percentage}
onGraphClick={key => {
  const graphKey = key as IReportCardKeys

  setActiveGraph(current =>
    current === graphKey ? undefined : graphKey
  )
}}
                />
              )
          )}
        </div>
      </WithHeaderSection>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  background-color: white;
`